import { useRef, useState } from "react";
import { Autocomplete, Box, Chip, TextField, Typography } from "@mui/material";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import { styled } from "@mui/system";
import PropTypes from "prop-types";

const iconStyles = { color: "#0000008a", cursor: "pointer", position: "absolute", right: 10, top: 10 };

const autocompleteStyles = {
  "& .MuiAutocomplete-endAdornment": { display: "none" },
  "& .MuiOutlinedInput-root": { padding: "0px !important" },
  "& .MuiAutocomplete-inputRoot": { padding: "0px" },
  "& .MuiAutocomplete-input": {
    width: "100% !important",
    paddingLeft: "8px !important",
    cursor: "pointer",
  },
  "& .MuiInputBase-input": { height: "27px !important" },
};

const StyledTextField = styled(TextField)(() => ({
  fontSize: "16px",
  "& .MuiOutlinedInput-root": {
    padding: "0px",
  },
  "& input, & input::placeholder, & .MuiInputBase-input": {
    fontSize: "16px !important",
  },
}));

const MultiSelectDropdown = ({
  options,
  selectedValues,
  setSelectedValues,
  error,
  placeholder,
  onChange,
  data,
  loading,
  renderChips = true, // New prop to control chip rendering
}) => {
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const inputRef = useRef(null);

  const handleDropdownToggle = () => {
    setIsDropdownOpen((prev) => !prev);
    inputRef.current?.focus();
  };

  const handleDeleteChip = (chipToDelete) => {
    const updatedValues = selectedValues.filter((item) => item !== chipToDelete);
    setSelectedValues(updatedValues);
  };

  return (
    <>
      <Autocomplete
        disablePortal
        open={isDropdownOpen}
        onOpen={() => setIsDropdownOpen(true)}
        onClose={() => setIsDropdownOpen(false)}
        value={selectedValues}
        options={loading ? [] : options}
        multiple
        getOptionLabel={(option) => {
          const optionData = data.find((opt) => opt.id === option);
          return optionData?.name || "";
        }}
        sx={autocompleteStyles}
        loading={loading}
        onChange={onChange}
        renderInput={(params) => (
          <StyledTextField
            placeholder={placeholder}
            variant="outlined"
            fullWidth
            {...params}
            ref={inputRef}
            InputProps={{
              ...params.InputProps,
              endAdornment: (
                <>
                  {isDropdownOpen ? (
                    <KeyboardArrowUpIcon sx={iconStyles} onClick={handleDropdownToggle} />
                  ) : (
                    <KeyboardArrowDownIcon sx={iconStyles} onClick={handleDropdownToggle} />
                  )}
                  {params.InputProps.endAdornment}
                </>
              ),
            }}
          />
        )}
        renderTags={() => null}
        renderOption={(props, option) => {
          const optionData = data.find((opt) => opt.id === option);
          return (
            <li {...props} key={option}>
              {optionData?.name || ""}
            </li>
          );
        }}
        noOptionsText={loading ? "Loading..." : "No options"}
      />

      {error && (
        <Typography variant="body2" color="error" sx={{ fontSize: 12, margin: "3px 14px 0px" }}>
          {error}
        </Typography>
      )}

      {renderChips && selectedValues.length > 0 && (
        <Box height="100px" bgcolor="white.light" borderRadius="12px" padding={1} overflow="auto" sx={{ marginTop: 2 }}>
          {selectedValues.map((id) => {
            const result = data.find((curr) => curr.id === id);
            return (
              <Chip
                key={id}
                label={result.name}
                onDelete={() => handleDeleteChip(id)}
                sx={{
                  bgcolor: "primary.main",
                  color: "common.white",
                  fontSize: "16px",
                  marginX: "2px",
                  marginY: "4px",
                  "& .MuiChip-deleteIcon": {
                    color: "common.white",
                    ":hover": {
                      color: "common.white",
                    },
                  },
                }}
              />
            );
          })}
        </Box>
      )}
    </>
  );
};

MultiSelectDropdown.propTypes = {
  options: PropTypes.arrayOf(PropTypes.string).isRequired,
  selectedValues: PropTypes.arrayOf(PropTypes.string).isRequired,
  setSelectedValues: PropTypes.func.isRequired,
  error: PropTypes.string,
  placeholder: PropTypes.string,
  onChange: PropTypes.func,
  data: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string.isRequired,
      name: PropTypes.string.isRequired,
    })
  ).isRequired,
  loading: PropTypes.bool,
  renderChips: PropTypes.bool,
};

export default MultiSelectDropdown;
